import { SearchOutlined } from '@ant-design/icons';
import { Input, Layout, Table, Tag } from 'antd';
import Column from 'antd/es/table/Column';
import type { SorterResult } from 'antd/es/table/interface';
import type { RangeValue } from 'rc-picker/es/interface';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { RangePicker } from 'app/components/fields/DatePicker';
import { ListPage } from 'app/components/lists/ListPage';
import { Pagination } from 'app/components/lists/Pagination';
import { dateTimeCell, dateTimeCell2 } from 'app/components/lists/cells';
import { ModalCheckBox } from 'app/components/pages/Extranet/ExtranetBookings/ModalCheckBox';
import type {
  BookingsQueryParams,
  BookingsSortingColumn,
} from 'app/hooks/data/core/admin/bookings/useBookings';
import {
  BOOKING_STATUS_TO_API,
  useBookings,
} from 'app/hooks/data/core/admin/bookings/useBookings';
import type { BookingListItem } from 'app/typings/core/admin/bookings/bookingList';
import type { BookingStatus } from 'app/typings/core/admin/bookings/enum/bookingStatus';
import { BOOKING_STATUS } from 'app/typings/core/admin/bookings/enum/bookingStatus';
import type { SupportedPsp } from 'app/typings/core/common/enum/supportedPsp';
import { SUPPORTED_PSP } from 'app/typings/core/common/enum/supportedPsp';
import type { SupportedPspMode } from 'app/typings/core/common/enum/supportedPspMode';
import { SUPPORTED_PSP_MODE } from 'app/typings/core/common/enum/supportedPspMode';
import { formatDateWithoutHour } from 'app/utils/dates';
import {
  dayjs,
  endOfISOWeek,
  startOfISOWeek,
  subMonths,
  subYears,
} from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import { scrollToTop } from 'app/utils/scroll';
import type { SortOrder } from 'app/utils/sort';
import { formatGatewaySort, getSortOrder } from 'app/utils/sort';

import 'styles/lists/left-right-header.scss';
import 'styles/pages/Booking/booking.scss';

type DateRange = {
  start: Date;
  end: Date;
};

type PaymentProvider = 'adyen' | 'stripe';
type PaymentMethod = 'payout' | 'vcc';

type TableState = {
  page: number;
  sortField?: string;
  sortOrder?: SortOrder;
  dateRange?: DateRange;
  status: BookingStatus[];
  paymentProvider: PaymentProvider[];
  paymentMethod: PaymentMethod[];
};

const STATUS_TAG: Record<BookingStatus, { color: string; label: string }> = {
  [BOOKING_STATUS.ACCEPTED]: { color: 'green', label: 'Accepted' },
  [BOOKING_STATUS.CANCELED]: { color: 'red', label: 'Cancelled' },
  [BOOKING_STATUS.PENDING]: { color: 'blue', label: 'Pending' },
  [BOOKING_STATUS.ABORTED]: { color: 'default', label: 'Aborted' },
};

const renderStatusTag = (status: BookingStatus) => {
  const { color, label } = STATUS_TAG[status] ?? {
    color: 'default',
    label: status,
  };

  return <Tag color={color}>{label}</Tag>;
};

const STATUS_OPTIONS = [
  { value: BOOKING_STATUS.ACCEPTED, label: 'Accepted' },
  { value: BOOKING_STATUS.CANCELED, label: 'Cancelled' },
  { value: BOOKING_STATUS.PENDING, label: 'Pending' },
  { value: BOOKING_STATUS.ABORTED, label: 'Aborted' },
] satisfies { value: BookingStatus; label: string }[];

const PAYMENT_PROVIDER_OPTIONS = [
  { value: 'adyen' as PaymentProvider, label: 'Adyen' },
  { value: 'stripe' as PaymentProvider, label: 'Stripe' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'payout' as PaymentMethod, label: 'Payout' },
  { value: 'vcc' as PaymentMethod, label: 'VCC' },
];

const DEFAULT_STATUS = [BOOKING_STATUS.ACCEPTED, BOOKING_STATUS.CANCELED];
const DEFAULT_PAYMENT_PROVIDER: PaymentProvider[] = ['adyen', 'stripe'];
const DEFAULT_PAYMENT_METHOD: PaymentMethod[] = ['payout', 'vcc'];

const SORTING_COLUMN_MAP: Partial<Record<string, BookingsSortingColumn>> = {
  createdAt: 'CREATED_AT',
  customerFirstName: 'FIRST_NAME',
  customerLastName: 'LAST_NAME',
};

const toApiPspFilter = (
  providers: PaymentProvider[]
): SupportedPsp | undefined => {
  if (providers.length === 1) {
    return providers[0] === 'adyen'
      ? SUPPORTED_PSP.ADYEN
      : SUPPORTED_PSP.STRIPE;
  }

  return undefined;
};

const toApiPspModeFilter = (
  methods: PaymentMethod[]
): SupportedPspMode | undefined => {
  if (methods.length === 1) {
    return methods[0] === 'vcc'
      ? SUPPORTED_PSP_MODE.ISSUING
      : SUPPORTED_PSP_MODE.PAYOUT;
  }

  return undefined;
};

const DEFAULT_DATE_RANGE: DateRange = {
  start: subMonths(new Date(), 1),
  end: new Date(),
};

const toDayjs = (date: Date): Dayjs => dayjs(date);

const fromDayjs = (m: Dayjs): Date => m.toDate();

const buildQueryParams = (
  state: TableState,
  search: string
): BookingsQueryParams => {
  const sortField = Array.isArray(state.sortField)
    ? state.sortField[state.sortField.length - 1]
    : state.sortField;

  return {
    search: search || undefined,
    sortingColumn: sortField ? SORTING_COLUMN_MAP[sortField] : undefined,
    sortingOrder: formatGatewaySort(state.sortOrder),
    pageNumber: state.page,
    beginning: state.dateRange
      ? formatDateWithoutHour(state.dateRange.start)
      : undefined,
    ending: state.dateRange
      ? formatDateWithoutHour(state.dateRange.end)
      : undefined,
    status: state.status
      .map((s) => BOOKING_STATUS_TO_API[s])
      .filter((s): s is NonNullable<typeof s> => s !== undefined),
    pspFilter: toApiPspFilter(state.paymentProvider),
    pspModeFilter: toApiPspModeFilter(state.paymentMethod),
  };
};

const parseStateFromURL = (searchParams: URLSearchParams): TableState => {
  const rawPage = parseInt(searchParams.get('page') ?? '1', 10);

  const rawStatus = searchParams.get('status');
  const status: BookingStatus[] = rawStatus
    ? (rawStatus
        .split(',')
        .filter((v) =>
          Object.values(BOOKING_STATUS).includes(v as BookingStatus)
        ) as BookingStatus[])
    : DEFAULT_STATUS;

  const rawProvider = searchParams.get('provider');
  const paymentProvider: PaymentProvider[] = rawProvider
    ? rawProvider
        .split(',')
        .filter((v): v is PaymentProvider =>
          DEFAULT_PAYMENT_PROVIDER.includes(v as PaymentProvider)
        )
    : DEFAULT_PAYMENT_PROVIDER;

  const rawMethod = searchParams.get('method');
  const paymentMethod: PaymentMethod[] = rawMethod
    ? rawMethod
        .split(',')
        .filter((v): v is PaymentMethod =>
          DEFAULT_PAYMENT_METHOD.includes(v as PaymentMethod)
        )
    : DEFAULT_PAYMENT_METHOD;

  const rawBeginning = searchParams.get('beginning');
  const rawEnding = searchParams.get('ending');
  const dateRange =
    rawBeginning && rawEnding
      ? { start: new Date(rawBeginning), end: new Date(rawEnding) }
      : DEFAULT_DATE_RANGE;

  return {
    page: isNaN(rawPage) ? 1 : rawPage,
    sortField: searchParams.get('sortField') || undefined,
    sortOrder: getSortOrder(searchParams.get('sortOrder') ?? '') || undefined,
    dateRange,
    status,
    paymentProvider,
    paymentMethod,
  };
};

export const BookingsList = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [tableState, setTableState] = useState<TableState>(() =>
    parseStateFromURL(searchParams)
  );

  const debouncedSearch = useDebounce(search, 500);
  const debouncedTableState = useDebounce(tableState, 300);

  const { data: bookingsData, isLoading } = useBookings(
    buildQueryParams(debouncedTableState, debouncedSearch)
  );

  useEffect(() => {
    const params = new URLSearchParams({
      search,
      page: String(tableState.page),
      sortField: tableState.sortField ?? '',
      sortOrder: tableState.sortOrder ?? '',
      beginning: tableState.dateRange
        ? formatDateWithoutHour(tableState.dateRange.start)
        : '',
      ending: tableState.dateRange
        ? formatDateWithoutHour(tableState.dateRange.end)
        : '',
      status: tableState.status.join(','),
      provider: tableState.paymentProvider.join(','),
      method: tableState.paymentMethod.join(','),
    });

    history.replace({ search: params.toString() });
  }, [tableState, search, history]);

  const handleDateChange = (range: RangeValue<Dayjs> | null) => {
    if (!range?.[0] || !range?.[1]) {
      setTableState((s) => ({ ...s, dateRange: undefined, page: 1 }));
    } else {
      setTableState((s) => ({
        ...s,
        dateRange: {
          start: fromDayjs(range[0] as Dayjs),
          end: fromDayjs(range[1] as Dayjs),
        },
        page: 1,
      }));
    }
  };

  const handleStatusChange = (status: BookingStatus[]) => {
    setTableState((s) => ({ ...s, status, page: 1 }));
  };

  const handleProviderChange = (providers: PaymentProvider[]) => {
    setTableState((s) => ({
      ...s,
      paymentProvider: providers.length ? providers : DEFAULT_PAYMENT_PROVIDER,
      page: 1,
    }));
  };

  const handleMethodChange = (methods: PaymentMethod[]) => {
    setTableState((s) => ({
      ...s,
      paymentMethod: methods.length ? methods : DEFAULT_PAYMENT_METHOD,
      page: 1,
    }));
  };

  const handlePageOffset = (delta: number) => {
    setTableState((s) => ({ ...s, page: s.page + delta }));
    scrollToTop();
  };

  const handleRowClick = ({ code, isLegacy }: BookingListItem) => ({
    onClick: () =>
      history.push(`bookings/${isLegacy ? `legacy/${code}` : code}`),
  });

  const handleSortChange = (
    _: unknown,
    __: unknown,
    sorter: SorterResult<BookingListItem> | SorterResult<BookingListItem>[]
  ) => {
    const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter;

    setTableState((s) => ({
      ...s,
      sortOrder: (order ?? undefined) as SortOrder | undefined,
      sortField: Array.isArray(field) ? field[1] : field,
    }));
  };

  const rangePickerValue: [Dayjs, Dayjs] | [null, null] = tableState.dateRange
    ? [toDayjs(tableState.dateRange.start), toDayjs(tableState.dateRange.end)]
    : [null, null];

  const today = new Date();
  const rangePickerPresets: Record<string, [Dayjs, Dayjs]> = {
    Today: [dayjs(), dayjs()],
    'This Week': [dayjs(startOfISOWeek(today)), dayjs(endOfISOWeek(today))],
    'Last month': [dayjs(subMonths(today, 1)), dayjs()],
    'Last 3 months': [dayjs(subMonths(today, 3)), dayjs()],
    'Last year': [dayjs(subYears(today, 1)), dayjs()],
  };

  const tableHeader = () => (
    <div className="left-right-header">
      <div className="left-right-header__left">
        <Input
          placeholder="Search here..."
          suffix={<SearchOutlined />}
          className="left-right-header__search"
          size="large"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <RangePicker
          size="large"
          value={rangePickerValue}
          onChange={handleDateChange}
          format="ddd DD MMM"
          ranges={rangePickerPresets}
        />
      </div>
      <Pagination
        className="left-right-header__right"
        pageNumber={tableState.page}
        pageResultsLength={bookingsData?.meta.itemCount}
        handleOffset={handlePageOffset}
        nbResults={bookingsData?.meta.itemCount}
      />
    </div>
  );

  return (
    <ListPage title="Bookings">
      <Layout className="booking">
        <Table<BookingListItem>
          title={tableHeader}
          loading={isLoading}
          onRow={handleRowClick}
          dataSource={bookingsData?.data}
          pagination={false}
          rowKey="code"
          onChange={handleSortChange}
        >
          <Column<BookingListItem>
            key="code"
            title="Code"
            dataIndex="code"
            render={(code: string) => code}
          />

          <Column<BookingListItem>
            title="Created at"
            sorter
            dataIndex="createdAt"
            render={dateTimeCell2}
          />

          <Column<BookingListItem>
            key="firstName"
            title="First Name"
            sorter
            dataIndex="customerFirstName"
          />

          <Column<BookingListItem>
            key="lastName"
            title="Last Name"
            sorter
            dataIndex="customerLastName"
          />

          <Column<BookingListItem>
            key="checkIn"
            title="Check in"
            dataIndex="checkIn"
            render={dateTimeCell('DD/MM/YYYY')}
          />

          <Column<BookingListItem>
            key="hotelName"
            title="Hotel"
            dataIndex="hotelName"
          />

          <Column<BookingListItem>
            key="status"
            className="sort-column"
            title={
              <ModalCheckBox
                title="Status"
                value={tableState.status}
                onChange={handleStatusChange}
                items={STATUS_OPTIONS}
              />
            }
            dataIndex="status"
            render={renderStatusTag}
          />
          <Column<BookingListItem>
            key="psp"
            className="sort-column"
            title={
              <ModalCheckBox
                title="Payment"
                value={tableState.paymentProvider}
                onChange={handleProviderChange}
                items={PAYMENT_PROVIDER_OPTIONS}
              />
            }
            dataIndex="psp"
            render={(psp: SupportedPsp) =>
              psp === SUPPORTED_PSP.ADYEN ? 'Adyen' : 'Stripe'
            }
          />
          <Column<BookingListItem>
            key="method"
            className="sort-column"
            title={
              <ModalCheckBox
                title="Method"
                value={tableState.paymentMethod}
                onChange={handleMethodChange}
                items={PAYMENT_METHOD_OPTIONS}
              />
            }
            dataIndex="pspMode"
            render={(pspMode: BookingListItem['pspMode']) =>
              pspMode === 'ISSUING' ? 'VCC' : 'Payout'
            }
          />
        </Table>

        <Pagination
          className="booking__footer"
          pageNumber={tableState.page}
          pageResultsLength={bookingsData?.meta.pageSize}
          handleOffset={handlePageOffset}
          nbResults={bookingsData?.meta.itemCount}
        />
      </Layout>
    </ListPage>
  );
};
